import React, { useState, useEffect } from 'react';
import AnimTabPanel from '../anim-tab-panel';
import AnimTabContent from '../anim-tab-content';
import { IAnimationEditorService } from '@src/slide/application/service/animation-editor-manager/core/interface';
import {
    PresetClassTypes,
    IAnimCategory,
    AnimEventType,
} from '@src/slide/domain/ui-model/element-modeles/property/animation/constants';
import { ISelectedAnimManager } from '@src/slide/application/service/selection/selected-anim-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IEditorAnimPreviewManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/editor-state-animation/editor-anim-preview-manager.interface';
import { DEFAULT_CLICK_SPID } from '@tencent/slide-sdk-model';
import { reaction } from 'mobx';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';

interface IProps {
    getAnimationEditorService: () => IDynamicService<IAnimationEditorService> | undefined;
    selectedAnimManager?: ISelectedAnimManager;
    selectionService?: ISelectionService;
    editorAnimPreviewManager?: IEditorAnimPreviewManager;
    presetClass: PresetClassTypes;
    animCategoryList: IAnimCategory[];
    title: string;
    num: number;
    showArrow?: boolean;
    totalWidth?: number;
    forceUpdateToolbarService?: Function;
}

const AnimCommonTabPanel = (props: IProps) => {
    const {
        selectedAnimManager,
        getAnimationEditorService,
        selectionService,
        editorAnimPreviewManager,
        presetClass,
        animCategoryList,
        title,
        num,
        showArrow,
        totalWidth,
        forceUpdateToolbarService = () => {},
    } = props;
    const [chosenId, setChosenId] = useState('');
    useEffect(() => {
        const disposeReaction = reaction(
            () => selectedAnimManager?.length,
            () => {
                const selectedAnimList = selectedAnimManager?.selectedArray;
                const animList = selectedAnimList?.reduce((previous, current) => {
                    if (current.props.presetClass === presetClass) {
                        return [...previous, current];
                    }
                    return previous;
                }, []);

                const id = animList?.length === 1 ? animList[0].props.animType : '';
                setChosenId(id);

                // workbench的bug，在一些场景下强制刷新会导致下拉组件的状态没有被重置为正确的状态
                // 进而引发下拉框被自动打开，bug表现可见tapd单【ID121792865】
                if (presetClass === PresetClassTypes.ENTR) {
                    setTimeout(() => {
                        forceUpdateToolbarService?.();
                    }, 100);
                }
            },
        );

        let disposeEvent;
        getAnimationEditorService()
            ?.promise()
            .then((service) => {
                disposeEvent = service.events.on(AnimEventType.MODIFY_ANIM_TYPE, ({ animType }) => {
                    setChosenId(animType);
                    forceUpdateToolbarService?.();
                });
            });

        return () => {
            disposeReaction?.();
            disposeEvent?.();
        };
    }, []);

    const handleClick = (id: string) => {
        const animConfig = animCategoryList
            .flatMap(animCategory => animCategory.children)
            .find(config => config.id === id);
        if (!animConfig) return;
        setChosenId(id);
        const selectedAnimList = selectedAnimManager?.selectedArray;
        if (!selectedAnimList?.length) {
            // 无选中的动画，则给元素添加动画
            getAnimationEditorService()
                ?.promise()
                .then((service) => {
                    service.addAnimation(animConfig.id, animConfig.presetClass);
                });
        } else {
            // 有选中的动画，则修改选中的动画
            selectedAnimList.forEach((animModel) => {
                if (!animModel.clickSpid) {
                    animModel.clickSpid = DEFAULT_CLICK_SPID;
                }
                selectionService?.currentSlide?.modifyAnimation(animModel, { animType: animConfig.id });
                const allowAutoPreview = editorAnimPreviewManager?.getAutoPreview();
                allowAutoPreview && editorAnimPreviewManager?.previewOneToOneAnims([animModel]);
            });
            getAnimationEditorService()
                ?.promise()
                .then((service) => {
                    service.events.emit(AnimEventType.MODIFY_ANIM_TYPE, { animType: animConfig.id });
                });
        }
    };

    return (
        <AnimTabPanel
            onClick={handleClick}
            animCategoryList={animCategoryList}
            title={title}
            num={num}
            TabContent={AnimTabContent}
            selectedId={chosenId}
            showArrow={showArrow}
            totalWidth={totalWidth}
        />
    );
};

export default AnimCommonTabPanel;
